/* eslint-disable no-console */
/* eslint-disable newline-before-return */
/* eslint-disable max-len */
import { CONTENT_TYPES } from '../../constants';
import { handleMultipleVideoUpdates } from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Creation/Video/VideoUploadHelper';
import { handleMultipleImageUpdates } from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Creation/Image/ImageUploadHelper';
import { handleDocumentUpdate } from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Creation/Document/DocumentUploadHelper';
import { toast } from 'react-toastify';

// Import podcast operations hook
import { usePodcastOperations } from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Creation/Podcast/hooks/usePodcastOperations';
import { updateArticle } from '../../../actions/article_management';

/**
 * Common function to filter out undefined/null/empty values from update options
 * @param options - Object containing update options
 * @returns Filtered object with only valid values
 */
const filterUpdateOptions = (options: any): any =>
{
	return Object.keys(options).reduce((filteredOptions, key) =>
	{
		const value = options[key];

		if (value !== undefined && value !== null && value !== '')
		{
			filteredOptions[key] = value;
		}

		return filteredOptions;
	}, {} as any);
};

/**
 * Updates local content data based on content type
 * @param prevData - Previous content data
 * @param updatedData - Updated data from slider
 * @param contentType - Type of content (videos, images, etc.)
 * @returns Updated content data
 */
export const updateLocalContentData = (
	prevData: any,
	updatedData: any,
	contentType: string
) =>
{
	switch (contentType)
	{
		case CONTENT_TYPES.VIDEOS:
			return {
				...prevData,
				// Collection-level properties
				categoryIds   : updatedData.categoryIds || prevData.categoryIds,
				tags          : updatedData.tags || prevData.tags,
				playlists     : updatedData.playlists || prevData.playlists,
				accessControl : updatedData.accessControl || prevData.accessControl,
				visibility    : updatedData.visibility || prevData.visibility,
				allowDownload : updatedData.allowDownload ?? prevData.allowDownload,
				allowShare    : updatedData.allowShare ?? prevData.allowShare,

				// Video-specific properties in videos array
				videos : prevData.videos?.map((video: any) => ({
					...video,
					title       : updatedData.title || video.title,
					description : updatedData.description || video.description,
					status      : updatedData.status || video.status,
					thumbnail   : {
						...video.thumbnail,
						url : updatedData.thumbnail?.url || video.thumbnail?.url
					}
				})) || []
			};

		case CONTENT_TYPES.IMAGES:
			return {
				...prevData,
				// Collection-level properties
				tags : updatedData.tags || prevData.tags,

				// Image-specific properties in files array
				files : prevData.files?.map((file: any) => ({
					...file,
					title         : updatedData.title || file.title,
					description   : updatedData.description || file.description,
					status        : updatedData.status || file.status,
					visibility    : updatedData.visibility || file.visibility,
					allowDownload : updatedData.allowDownload ?? file.allowDownload,
					allowShare    : updatedData.allowShare ?? file.allowShare,
					accessControl : updatedData.accessControl || file.accessControl,
					categoryIds   : updatedData.categoryIds || file.categoryIds,
					thumbnail     : {
						...file.thumbnail,
						url : updatedData.thumbnail?.url || file.thumbnail?.url
					}
				})) || []
			};

		case CONTENT_TYPES.DOCUMENTS:
			return {
				...prevData,
				// Document-specific update logic will be added here
				tags  : updatedData.tags || prevData.tags,
				files : prevData.files?.map((file: any) => ({
					...file,
					title         : updatedData.title || file.title,
					description   : updatedData.description || file.description,
					status        : updatedData.status || file.status,
					visibility    : updatedData.visibility || file.visibility,
					allowDownload : updatedData.allowDownload ?? file.allowDownload,
					allowShare    : updatedData.allowShare ?? file.allowShare,
					accessControl : updatedData.accessControl || file.accessControl,
					categoryIds   : updatedData.categoryIds || file.categoryIds
				})) || []
			};

		case CONTENT_TYPES.PODCASTS:
			// Future implementation for podcasts
			return {
				...prevData,
				// Podcast-specific update logic will be added here
				categoryIds   : updatedData.categoryIds || prevData.categoryIds,
				tags          : updatedData.tags || prevData.tags,
				accessControl : updatedData.accessControl || prevData.accessControl,
				visibility    : updatedData.visibility || prevData.visibility,
				title         : updatedData.title || prevData.title,
				description   : updatedData.description || prevData.description,
				status        : updatedData.status
			};

		case CONTENT_TYPES.ARTICLES:
			return {
				...prevData,
				// Article-specific properties
				title         : updatedData.title || prevData.title,
				summary       : updatedData.summary || prevData.summary,
				rich_content  : updatedData.rich_content || prevData.rich_content,
				category      : updatedData.category || prevData.category,
				subCategory   : updatedData.subCategory || prevData.subCategory,
				tags          : updatedData.tags || prevData.tags,
				visibility    : updatedData.visibility || prevData.visibility,
				allowDownload : updatedData.allowDownload ?? prevData.allowDownload,
				allowShare    : updatedData.allowShare ?? prevData.allowShare,
				accessControl : updatedData.accessControl || prevData.accessControl,
				status        : updatedData.status
			};

		default:
			console.warn(`Unsupported content type for local update: ${contentType}`);

			return prevData;
	}
};

/**
 * Handles updating multiple articles with new details
 * @param companyId - The company ID
 * @param articles - Array of article IDs to update
 * @param updatedData - Updated data for the articles
 * @returns Promise with the updated article data
 */
export const handleMultipleArticleUpdates = async (
	companyId: string,
	articles: string[],
	updatedData: any
) =>
{
	if (!articles || articles.length === 0)
	{
		throw new Error('No article IDs provided for update');
	}

	try
	{
		// Show an initial toast for starting the update
		const isBatchUpdate = articles.length > 1;

		// Process each article update as a separate operation
		const updatePromises = articles.map((articleItem) =>
		{
			// Extract full article object or fallback if only ID is provided
			const isStringId = typeof articleItem === 'string';
			const existingData: any = isStringId ? {} : articleItem; // fallback for various formats
			const articleId = isStringId ? articleItem : existingData.id || existingData._id;

			const isBulk = Array.isArray(articles) && articles.length > 1 && !isStringId;

			console.log(existingData, 'existingData');
			console.log(updatedData, 'updatedData');

			// Array merging logic only for bulk
			const mergedTags = isBulk
				? Array.from(new Set([
					...(existingData.tags || []),
					...(updatedData.tags || [])
				]))
				: updatedData.tags;

			// Check if categoryIds have actually changed to prevent "Content already belongs to this category" error
			const existingCategoryIds = existingData?.categoryIds || [];
			const newCategoryIds = updatedData?.categoryIds || [];
			
			// Helper function to compare arrays
			const areArraysEqual = (arr1: string[], arr2: string[]) => {
				if (arr1.length !== arr2.length) return false;
				const sorted1 = [...arr1].sort();
				const sorted2 = [...arr2].sort();
				return sorted1.every((value, index) => value === sorted2[index]);
			};
			
			// Only include categoryIds if they've actually changed
			const shouldIncludeCategoryIds = !areArraysEqual(existingCategoryIds, newCategoryIds);

			// Create options without categoryIds first
			const { categoryIds, ...updatedDataWithoutCategoryIds } = updatedData;

			const options: any = {
				// Use updated values if present, fallback to existing (for bulk)
				title         : updatedData.title ?? (isBulk ? existingData.title : undefined),
				summary       : updatedData.summary ?? (isBulk ? existingData.summary : undefined),
				rich_content  : updatedData.rich_content ?? (isBulk ? existingData.rich_content : undefined),
				category      : updatedData.category ?? (isBulk ? existingData.category : undefined),
				subCategory   : updatedData.subCategory ?? (isBulk ? existingData.subCategory : undefined),
				visibility    : updatedData.visibility ?? (isBulk ? existingData.visibility : undefined),
				allowDownload : updatedData.allowDownload ?? (isBulk ? existingData.allowDownload : undefined),
				allowShare    : updatedData.allowShare ?? (isBulk ? existingData.allowShare : undefined),
				accessControl : updatedData.accessControl ?? (isBulk ? existingData.accessControl : undefined),
				status        : updatedData.status ?? (isBulk ? existingData.status : undefined),
				tags          : mergedTags,
				// Only include categoryIds if they've changed
				...(shouldIncludeCategoryIds && { categoryIds: newCategoryIds })
			};

			// Handle scheduled publish date
			if (updatedData.publishDate || updatedData.scheduledPublishDate)
			{
				options.scheduledPublishDate = updatedData.publishDate || updatedData.scheduledPublishDate;
			}

			// Clean up undefined fields
			Object.keys(options).forEach((key) =>
			{
				if (options[key as keyof typeof options] === undefined)
				{
					delete options[key as keyof typeof options];
				}
			});

			console.log(options, '---final article options');

			return updateArticle(articleId, options);
		});

		// Wait for all updates to complete
		const responses = await Promise.all(updatePromises);

		// Show success message
		toast.success(
			isBatchUpdate
				? `${articles.length} articles updated successfully!`
				: 'Article updated successfully!'
		);

		return responses;
	}
	catch (error)
	{
		console.error('Error updating articles:', error);

		// Provide error message
		const errorMessage = articles.length > 1
			? `Failed to update some or all of the ${articles.length} articles`
			: 'Failed to update article';

		toast.error(errorMessage);
		throw error;
	}
};

/**
 * Handles content updates based on content type
 * @param updatedData - Updated data from slider
 * @param contentIds - Array of content IDs to update
 * @param contentData - Original content data
 * @param contentType - Type of content
 * @param updateLocalState - Function to update local state
 * @param closeSlider - Function to close the slider
 * @returns Promise with API response
 */
export const handleContentUpdate = async (
	updatedData: any,
	contentIds: any[],
	contentData: any,
	contentType: any,
	updateLocalState: (data: any) => void,
	closeSlider: () => void
) =>
{
	if (!contentIds || contentIds.length === 0)
	{
		throw new Error('No content IDs provided for update');
	}

	const companyId = contentData?.companyId || '';

	// Update local state immediately (optimistic update)
	updateLocalState(updatedData);

	// Close slider
	console.log('contentType', contentType);
	closeSlider();

	// Handle API calls based on content type
	switch (contentType)
	{
		case (CONTENT_TYPES.VIDEOS):
		{
			// Check if categoryIds have actually changed to prevent "Content already belongs to this category" error
			const existingCategoryIds = contentData?.categoryIds || [];
			const newCategoryIds = updatedData?.categoryIds || [];
			
			// Helper function to compare arrays
			const areArraysEqual = (arr1: string[], arr2: string[]) => {
				if (arr1.length !== arr2.length) return false;
				const sorted1 = [...arr1].sort();
				const sorted2 = [...arr2].sort();
				return sorted1.every((value, index) => value === sorted2[index]);
			};
			
			// Only include categoryIds if they've actually changed AND are explicitly provided
			const shouldIncludeCategoryIds = updatedData.hasOwnProperty('categoryIds') && !areArraysEqual(existingCategoryIds, newCategoryIds);
			
			// Create updatedData without categoryIds first
			const { categoryIds, ...updatedDataWithoutCategoryIds } = updatedData;
			
			const videoUpdateOptions = {
				...updatedDataWithoutCategoryIds,
				title         : updatedData.title,
				description   : updatedData.description,
				// visibility    : updatedData.visibility,
				...(shouldIncludeCategoryIds && { categoryIds: newCategoryIds }),
				tags          : updatedData.tags,
				playlists     : updatedData.playlists,
				thumbnail     : updatedData.thumbnail,
				allowDownload : updatedData.allowDownload,
				allowShare    : updatedData.allowShare,
				// accessControl : updatedData.accessControl,
				url           : contentData?.videos?.[0]?.url,
				fileType      : contentData?.videos?.[0]?.fileType,
				fileSize      : contentData?.videos?.[0]?.fileSize,
				publishDate   : updatedData.publishDate // Add publishDate field
			};

			// Filter out undefined/null/empty values using common function
			const filteredOptions = filterUpdateOptions(videoUpdateOptions);

			// Only proceed with API call if we have valid options
			if (Object.keys(filteredOptions).length > 0)
			{
				return handleMultipleVideoUpdates(companyId, contentIds, filteredOptions, contentData);
			}
			else
			{
				return Promise.resolve({ success: true, message: 'No valid video update data provided' });
			}
		}

		case CONTENT_TYPES.IMAGES:
		{
			// Create image update options similar to how the hook formats data
			const imageUpdateOptions: any = {
				title         : updatedData.title,
				description   : updatedData.description,
				url           : updatedData.url || contentData?.files?.[0]?.url,
				fileType      : updatedData.fileType || contentData?.files?.[0]?.fileType,
				fileSize      : updatedData.fileSize || contentData?.files?.[0]?.fileSize,
				dimensions    : updatedData.dimensions || contentData?.files?.[0]?.dimensions,
				status        : updatedData.status,
				// visibility    : updatedData.visibility,
				categoryIds   : updatedData.categoryIds,
				tags          : updatedData.tags,
				allowDownload : updatedData.allowDownload,
				allowShare    : updatedData.allowShare,
				// accessControl : updatedData.accessControl,
				quality       : updatedData.quality,
				publishDate   : updatedData.publishDate
			};

			// Handle thumbnail specially - map thumbnail.url to thumbnailUrl (like the hook does)
			if (updatedData.thumbnail?.url)
			{
				imageUpdateOptions.thumbnail = updatedData.thumbnail;
			}

			// Filter out undefined/null/empty values (like the hook does)
			const filteredOptions = filterUpdateOptions(imageUpdateOptions);

			// Only proceed with API call if we have valid options
			if (Object.keys(filteredOptions).length > 0)
			{
				return handleMultipleImageUpdates(companyId, contentIds, filteredOptions, contentData);
			}
			else
			{
				return Promise.resolve({ success: true, message: 'No valid image update data provided' });
			}
		}

		case CONTENT_TYPES.DOCUMENTS:
		{
			// Check if categoryIds have actually changed to prevent "Content already belongs to this category" error
			const existingCategoryIds = contentData?.categoryIds || [];
			const newCategoryIds = updatedData?.categoryIds || [];
			
			// Helper function to compare arrays
			const areArraysEqual = (arr1: string[], arr2: string[]) => {
				if (arr1.length !== arr2.length) return false;
				const sorted1 = [...arr1].sort();
				const sorted2 = [...arr2].sort();
				return sorted1.every((value, index) => value === sorted2[index]);
			};
			
			// Only include categoryIds if they've actually changed
			const shouldIncludeCategoryIds = !areArraysEqual(existingCategoryIds, newCategoryIds);
			
			// Create updatedData without categoryIds first
			const { categoryIds, ...updatedDataWithoutCategoryIds } = updatedData;
			
			const documentUpdateOptions: any = {
				...updatedDataWithoutCategoryIds,
				title         : updatedData.title,
				description   : updatedData.description,
				url           : updatedData.url || contentData?.files?.[0]?.url,
				fileType      : updatedData.fileType || contentData?.files?.[0]?.fileType,
				fileSize      : updatedData.fileSize || contentData?.files?.[0]?.fileSize,
				pageCount     : updatedData.pageCount || contentData?.files?.[0]?.pageCount,
				// visibility    : updatedData.visibility,
				...(shouldIncludeCategoryIds && { categoryIds: newCategoryIds }),
				tags          : updatedData.tags,
				allowDownload : updatedData.allowDownload,
				allowShare    : updatedData.allowShare,
				// accessControl : updatedData.accessControl,
				status        : updatedData.status,
				metadata      : updatedData.metadata
			};

			// Handle thumbnail specially if it exists
			if (updatedData.thumbnail?.url)
			{
				documentUpdateOptions.thumbnail = updatedData.thumbnail;
			}

			// Filter out undefined/null/empty values using common function
			const filteredOptions = filterUpdateOptions(documentUpdateOptions);

			// Only proceed with API call if we have valid options
			if (Object.keys(filteredOptions).length > 0)
			{
				console.log('contentIds', contentIds);
				return handleDocumentUpdate(companyId, contentIds, filteredOptions);
			}
			else
			{
				return Promise.resolve({ success: true, message: 'No valid document update data provided' });
			}
		}

		case CONTENT_TYPES.PODCASTS:
		{
			// Check if categoryIds have actually changed to prevent "Content already belongs to this category" error
			const existingCategoryIds = contentData?.categoryIds || [];
			const newCategoryIds = updatedData?.categoryIds || [];
			
			// Helper function to compare arrays
			const areArraysEqual = (arr1: string[], arr2: string[]) => {
				if (arr1.length !== arr2.length) return false;
				const sorted1 = [...arr1].sort();
				const sorted2 = [...arr2].sort();
				return sorted1.every((value, index) => value === sorted2[index]);
			};
			
			// Only include categoryIds if they've actually changed AND are explicitly provided
			const shouldIncludeCategoryIds = updatedData.hasOwnProperty('categoryIds') && !areArraysEqual(existingCategoryIds, newCategoryIds);
			
			// Create updatedData without categoryIds first
			const { categoryIds, ...updatedDataWithoutCategoryIds } = updatedData;
			
			// Use the podcast operations hook
			const { handleMultiplePodcastUpdates } = usePodcastOperations();

			const podcastUpdateOptions: any = {
				...updatedDataWithoutCategoryIds,
				title         : updatedData.title,
				description   : updatedData.description,
				// visibility    : updatedData.visibility,
				...(shouldIncludeCategoryIds && { categoryIds: newCategoryIds }),
				tags          : updatedData.tags,
				allowDownload : updatedData.allowDownload,
				allowShare    : updatedData.allowShare,
				// accessControl : updatedData.accessControl,
				status        : updatedData.status,
				sourceType    : updatedData.sourceType
			};

			// Handle coverImage specially if it exists
			if (updatedData.coverImage?.url)
			{
				podcastUpdateOptions.coverImage = updatedData.coverImage;
			}

			// Handle scheduled publish date
			if (updatedData.publishDate || updatedData.scheduledPublishDate)
			{
				podcastUpdateOptions.scheduledPublishDate = updatedData.publishDate || updatedData.scheduledPublishDate;
			}

			// Filter out undefined/null/empty values using common function
			const filteredOptions = filterUpdateOptions(podcastUpdateOptions);

			// Only proceed with API call if we have valid options
			if (Object.keys(filteredOptions).length > 0)
			{
				console.log('filteredOptions', filteredOptions);
				// Use the hook's function with no toasts for content card usage
				return handleMultiplePodcastUpdates(companyId, contentIds, filteredOptions, { showToasts: false });
			}
			else
			{
				return Promise.resolve({ success: true, message: 'No valid podcast update data provided' });
			}
		}

		case CONTENT_TYPES.ARTICLES:
		{
			// Check if categoryIds have actually changed to prevent "Content already belongs to this category" error
			const existingCategoryIds = contentData?.categoryIds || [];
			const newCategoryIds = updatedData?.categoryIds || [];
			
			// Helper function to compare arrays
			const areArraysEqual = (arr1: string[], arr2: string[]) => {
				if (arr1.length !== arr2.length) return false;
				const sorted1 = [...arr1].sort();
				const sorted2 = [...arr2].sort();
				return sorted1.every((value, index) => value === sorted2[index]);
			};
			
			// Only include categoryIds if they've actually changed
			const shouldIncludeCategoryIds = !areArraysEqual(existingCategoryIds, newCategoryIds);
			
			// Create updatedData without categoryIds first
			const { categoryIds, ...updatedDataWithoutCategoryIds } = updatedData;
			
			const articleUpdateOptions: any = {
				...updatedDataWithoutCategoryIds,
				title         : updatedData.title,
				summary       : updatedData.summary,
				rich_content  : updatedData.rich_content,
				category      : updatedData.category,
				subCategory   : updatedData.subCategory,
				...(shouldIncludeCategoryIds && { categoryIds: newCategoryIds }),
				tags          : updatedData.tags,
				// visibility    : updatedData.visibility,
				allowDownload : updatedData.allowDownload,
				allowShare    : updatedData.allowShare,
				// accessControl : updatedData.accessControl,
				status        : updatedData.status
			};

			// Handle scheduled publish date
			if (updatedData.publishDate || updatedData.scheduledPublishDate)
			{
				articleUpdateOptions.scheduledPublishDate = updatedData.publishDate || updatedData.scheduledPublishDate;
			}

			// Filter out undefined/null/empty values using common function
			const filteredOptions = filterUpdateOptions(articleUpdateOptions);

			// Only proceed with API call if we have valid options
			if (Object.keys(filteredOptions).length > 0)
			{
				console.log('Article update options:', filteredOptions);
				return handleMultipleArticleUpdates(companyId, contentIds, filteredOptions);
			}
			else
			{
				return Promise.resolve({ success: true, message: 'No valid article update data provided' });
			}
		}

		default:
			throw new Error(`Unsupported content type for API update: ${contentType}`);
	}
};
